import "./style.scss";
import "react-pdf/dist/esm/Page/TextLayer.css";

import { subject } from "@casl/ability";
import { TabContext, TabPanel } from "@mui/lab";
import { CircularProgress, Tab } from "@mui/material";
import ActionCrossSign from "assets/img/svg/actionCrossSign";
import ActionDownload from "assets/img/svg/actionDownload";
import ActionSend from "assets/img/svg/actionSend";
import Edit from "assets/img/svg/edit";
import ModalBox from "components/Modal";
import { PortalType } from "contexts/portal.context";
import { Body, BodyHighlight, SmallBody } from "helpers/TypographyComponents";
import { downloadFile, formatLocaleDateString, formatNumber, getStatementFilename } from "helpers/utils";
import { useAbility } from "hooks/useAbility";
import { resolveArticleCalculationBase } from "helpers/pressCalculationBase";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useIsMobile } from "hooks/useIsMobile";
import useWindowSize from "hooks/useWindowSize";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Document, Page, pdfjs } from "react-pdf";
import { useNavigate } from "react-router-dom";
import { AgreementSignatureStatus, AgreementStatus, fetchAgreements, resetAgreement } from "store/agreementsStore";

import { hasContractFile, isContractTabDisabled, isStatementNotTransmitted, showContractTab } from "./utils";
import type { IArticle } from "store/articleStore";
import {
    deleteManualContract,
    getAllArticles,
    getAllArticlesBeneficiars,
    putArticlePaid,
    sendManualContract,
    setContractSigned,
    setLastSentAtTerms,
} from "store/articleStore";
import { fetchBeneficiar } from "store/beneficiaryStore";
import { getServices } from "store/servicesStore";
import {
    fetchAndSaveStatement,
    fetchContract,
    fetchContractDownload,
    fetchNetStatement,
    fetchPrecountStatement,
    sendMailWithStatement,
} from "store/statementsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { ChannelType, PaymentStatus } from "store/types";

import BPArticleAgreements from "../BPArticleAgreements";
import ContractsCounterBlock from "../ContractsCounterBlock";
import DeleteModal from "../DeleteModal";
import EmptyState from "../EmptyState";
import LoadingWrapper from "../LoadingWrapper";
import PDFViewer from "../ResponsivePDFViewer";
import SendEmail from "../SendEmail";
import { Spinner } from "../Spinner";
import StatusSelect from "../StatusSelect";
import type { PaymentOption } from "../StatusSelect/utils";
import { paymentColorRules, paymentOptions } from "../StatusSelect/utils";
import ButtonMenu, { type Action } from "../ButtonMenu";
import Plus from "assets/img/svg/plus";
import { useActiveSignProvider } from "hooks/useActiveSignProvider";
import type { NewAgreementLocationState } from "containers/ContractValidationNew";
import StyledButton from "../StyledButton";
import { StyledDatePicker } from "../StyledDatePicker";
import { StyledTabs } from "../StyledTabs";
import StyledTextField from "../StyledTextField/StyledTextField";
import DropZoneCSV from "../UploadCsv";

interface ArticleDetailContentProps {
    article: IArticle | null | undefined;
    onClose: () => void;
    editMode: boolean;
    onEditMode: () => void;
    from: "publication" | "beneficiary" | "management";
    handleShowAgreementModal: () => void;
    currentTab: any;
    onTabChange: (_, tab) => void;
}

// problem with import.meta.url, it causes jest to fail tests
// possible solution to keep the local worker
// https://stackoverflow.com/questions/76386296/web-workers-in-create-react-app-jest-error-cannot-use-import-meta-outside-a
// pdfjs.GlobalWorkerOptions.workerSrc = new URL(
//     'pdfjs-dist/build/pdf.worker.min.js',
//     import.meta.url,
// ).toString();

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const statementType = [
    { labelKey: "Summary.StatementOfNetDuties", type: "net" },
    { labelKey: "Summary.PrepaymentCertification", type: "precount" },
    { labelKey: "Summary.Contract" },
];

enum Tabs {
    NET_STATEMENT = 0,
    PRECOUNT_STATEMENT = 1,
    CONTRACT = 2,
}

const ArticleDetailContent = ({
    article,
    onClose,
    editMode,
    onEditMode,
    from,
    handleShowAgreementModal,
    currentTab,
    onTabChange,
}: ArticleDetailContentProps) => {
    const portalType = useContext(PortalType);
    const { getCompanySettings, isCompany } = useCompanySettings();
    const usesAgreements = !!getCompanySettings("uses_agreements");
    const usesAgreementValidation = getCompanySettings("uses_agreement_validation_chain");
    const usesIRCEC = getCompanySettings("uses_ircec");
    const { sendSignatureStatus, contractSigned } = useAppSelector((state) => state.article);
    const { agreements } = useAppSelector((state) => state.agreements);
    const { agreementsStatus } = useAppSelector((state) => state.agreements.agFetchStatus);
    const { contractId } = article ?? {};

    const articleAgreements = useMemo(
        () =>
            article
                ? agreements.filter((a) => a.relatedResource === "article" && +a.relatedResourceId === +article.id)
                : [],
        [agreements, article],
    );
    const currentAgreement = useMemo(
        () =>
            articleAgreements
                .slice()
                .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())
                .find((a) => a.status !== AgreementStatus.DRAFT) ?? articleAgreements[0],
        [articleAgreements],
    );
    const { services, servicesStatus } = useAppSelector((state) => state.services);
    const workNatureIRCEC = services.find((s) => s.id === article?.ircecProfileId)?.name;
    const ability = useAbility();

    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const windowSize = useWindowSize();

    const { isMobile } = useIsMobile();
    const { status, netStatement, precountStatement, contract, savingStatus } = useAppSelector(
        (state) => state.statements,
    );
    const { beneficiar } = useAppSelector((state) => state.beneficiary);
    const { channels } = useAppSelector((state) => state.channels);

    const [paidStatus, setPaidStatus] = useState(article?.paidStatus);
    const [openDeleteContractModal, setOpenDeleteContractModal] = useState(false);
    const [openCancelSentContractModal, setOpenCancelSentContractModal] = useState(false);

    const [showUploadContractModal, setShowUploadContractModal] = useState(false);
    const activeSignProvider = useActiveSignProvider();

    useEffect(() => {
        if (usesIRCEC && servicesStatus === "idle") {
            dispatch(getServices());
        }
        dispatch(setContractSigned(article?.contractSigned));
    }, []);

    useEffect(() => {
        if (article?.id && portalType !== "beneficiary") dispatch(fetchAgreements());
    }, [article?.id, dispatch, portalType]);

    const [pdfHeight, setPdfHeight] = useState<number | null>(null);
    const statementPdfRef = useCallback((node) => {
        if (node !== null) setPdfHeight(node.offsetHeight);
    }, []);

    const [signatureDate, setSignatureDate] = useState(new Date());
    const [file, setFile] = useState<any>();
    const hasAuthorPortalAccess = article?.accountStatus && article?.accountStatus == "confirmed";
    const sendTermText = hasAuthorPortalAccess
        ? article.lastSentAt
            ? t("Articles.AlreadySentToBeneficiarPortal")
            : t("Articles.SendToBeneficiarPortal")
        : t("Articles.SendToBeneficiar");
    const disabledSendText =
        hasAuthorPortalAccess && !!article.lastSentAt
            ? t("Articles.AlreadySent")
            : !article?.email
              ? t("Articles.NoEmail")
              : sendTermText;
    const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);

    const articleChannel = channels.find((channel) => channel.id === article?.channelId);
    const isFolioBased =
        resolveArticleCalculationBase({
            calculationBase: article?.calculationBase,
            channel: articleChannel,
        }) === "folio";

    const [manualContractUploaded, setManualContractUploaded] = useState(
        article?.contractId && article?.contractId.includes("crealo:"),
    );

    const dropdownPaymentOptions = (): PaymentOption[] => {
        // High level access: can set all payment options
        if (ability.can("access", subject("ArticlePaymentOptions", { level: 2 }))) {
            return paymentOptions();
        } else if (ability.can("access", subject("ArticlePaymentOptions", { level: 1 }))) {
            return [
                { label: t("Articles.NotPaid"), value: PaymentStatus.NOT_PAID },
                { label: t("Articles.ToPay"), value: PaymentStatus.TO_PAY },
            ];
        }

        return [];
    };

    const disablePaymentSelect = (value: PaymentStatus) => {
        if (value === PaymentStatus.PAID) {
            return ability.cannot("access", subject("ArticlePaymentOptions", { level: 2 }));
        } else {
            return ability.cannot("access", subject("ArticlePaymentOptions", { level: 1 }));
        }
    };

    useEffect(() => {
        if (!article) return;
        switch (+currentTab) {
            case Tabs.NET_STATEMENT:
                if (statementNotTransmitted) break;
                dispatch(
                    fetchNetStatement({
                        articleId: article.id,
                        companyId: article.companyId,
                    }),
                );
                break;
            case Tabs.PRECOUNT_STATEMENT:
                if (statementNotTransmitted) break;
                dispatch(
                    fetchPrecountStatement({
                        articleId: article.id,
                        companyId: article.companyId,
                    }),
                );
                break;
            case Tabs.CONTRACT: {
                dispatch(
                    fetchContract({
                        titleId: article.titleId,
                        articleId: article.id,
                        companyId: article.companyId,
                    }),
                );
            }
        }
    }, [currentTab, article]);

    useEffect(() => {
        if (!["audiovisual", "press"].includes(portalType)) return;
        if (!Object.keys(beneficiar).length && article) dispatch(fetchBeneficiar(article.beneficiaryId));
    }, []);

    const handlePaymentStatus = (value: 0 | 1 | 2, id: number) => {
        const paymentData = {
            paidStatus: value,
            paymentDate: value == 0 ? null : new Date(),
            from,
        };
        if (article)
            dispatch(
                putArticlePaid({
                    titleId: article?.titleId,
                    articleId: article.id,
                    paymentData,
                }),
            );
        setPaidStatus(value);
    };

    const savePdf = useCallback(() => {
        if (savingStatus == "pending") return;
        const filename = getStatementFilename(
            article?.beneficiary ?? "",
            article?.titleName ?? "",
            t(statementType[+currentTab].labelKey),
            new Date(),
        );
        if (currentTab < "2") {
            dispatch(
                fetchAndSaveStatement({
                    articleId: article?.id,
                    companyId: article?.companyId,
                    fileName: filename,
                    statementType: statementType[+currentTab].type,
                    type: "press",
                }),
            );
        } else if (
            currentTab == "2" &&
            article?.contractSigned != undefined &&
            article?.contractSigned == AgreementSignatureStatus.TO_SIGN
        ) {
            dispatch(
                fetchContractDownload({
                    titleId: article.titleId,
                    articleId: article.id,
                    download: true,
                    filename,
                    companyId: article.companyId,
                }),
            );
        } else if (currentTab == "2" && hasContractFile(article)) {
            const linkSource = `data:application/pdf;base64,${contract}`;
            downloadFile({ source: linkSource, fileName: filename, isLink: true });
        }
    }, [dispatch, savingStatus, currentTab, article, contract]);

    const handleUploadManualSignedContract = () => {
        if (!article) return;
        if (file && file.length > 0 && signatureDate) {
            const formData = new FormData();
            formData.append("file", file[0], file[0].name);
            dispatch(
                sendManualContract({
                    titleId: article.titleId,
                    articleId: article.id,
                    file: formData,
                    signatureDate,
                    from,
                }),
            ).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    setManualContractUploaded(true);
                    dispatch(setContractSigned(AgreementSignatureStatus.SIGNED));
                    if (from === "beneficiary") {
                        dispatch(getAllArticlesBeneficiars(article?.beneficiaryId));
                    } else {
                        dispatch(getAllArticles(article?.titleId));
                    }
                }
            });
        }
        setShowUploadContractModal(false);
    };

    const sendEmail = (emails: string[], subject: string, body: string) => {
        if (!article) return;
        dispatch(
            sendMailWithStatement({
                articleId: article.id,
                mailCc: emails,
                subject,
                body,
            }),
        );
        dispatch(setLastSentAtTerms([article.id]));
        setIsEmailModalOpen(false);
    };

    const handleDeleteManualContract = async () => {
        if (article?.titleId && article?.id) {
            const response = await dispatch(
                deleteManualContract({ titleId: article?.titleId, articleId: article?.id, from }),
            );
            if (response.meta.requestStatus === "fulfilled") {
                setManualContractUploaded(false);
                setOpenDeleteContractModal(false);
                dispatch(setContractSigned(AgreementSignatureStatus.TO_SIGN));
                dispatch(resetAgreement());
                dispatch(
                    from === "beneficiary"
                        ? dispatch(getAllArticlesBeneficiars(article?.beneficiaryId))
                        : dispatch(getAllArticles(article?.titleId)),
                );
            }
        }
    };

    const handleCancelSentContract = async () => {
        if (!article?.titleId || !article?.id) return;

        const response = await dispatch(
            deleteManualContract({
                titleId: article.titleId,
                articleId: article.id,
                from,
                deleteType: "sent",
            }),
        );

        if (response.meta.requestStatus === "fulfilled") {
            setOpenCancelSentContractModal(false);
            dispatch(setContractSigned(AgreementSignatureStatus.TO_SIGN));
            dispatch(resetAgreement());
            dispatch(
                from === "beneficiary"
                    ? dispatch(getAllArticlesBeneficiars(article.beneficiaryId))
                    : dispatch(getAllArticles(article.titleId)),
            );
        }
    };

    /** Scale term size for mobile & tablets */
    const scalePDFValue = useMemo(() => {
        const WIDTH_PDF = 914;
        return windowSize.width / WIDTH_PDF;
    }, [windowSize]);

    const muiTabHeight = useMemo(() => {
        if (pdfHeight) {
            const muiTabHeight = pdfHeight * scalePDFValue + 150;
            return muiTabHeight;
        }
    }, [windowSize, currentTab, pdfHeight, scalePDFValue]);

    const contractTabVisible = showContractTab(portalType, article);
    const contractTabDisabled = isContractTabDisabled(portalType, article);
    const statementNotTransmitted = isStatementNotTransmitted(portalType, article);

    if (!article) return <></>;

    return (
        <div className="article-detail-content">
            <DeleteModal
                open={openDeleteContractModal}
                onClose={() => setOpenDeleteContractModal(false)}
                onDelete={handleDeleteManualContract}
                elementTitle={t("ShowDetails.TheContract")}
            />
            <DeleteModal
                open={openCancelSentContractModal}
                onClose={() => setOpenCancelSentContractModal(false)}
                onDelete={handleCancelSentContract}
                elementTitle={t("ShowDetails.TheContract")}
                title={t("Articles.CancelContractSendModalTitle")}
                confirmLabel={t("Articles.CancelContractSendConfirm")}
                description={t("Articles.CancelContractSendDescription")}
            />
            {["audiovisual", "press"].includes(portalType) && (
                <SendEmail
                    isModalOpen={isEmailModalOpen}
                    termData={{
                        beneficiaryId: article?.beneficiaryId,
                        author: article?.beneficiary,
                        title: article?.name,
                        email: article?.email,
                        precount: `${article?.precount}`,
                        contractSigned: article.contractSigned,
                        issue: article.titleName,
                    }}
                    onClose={() => setIsEmailModalOpen(false)}
                    sendEmail={sendEmail}
                    hasAuthorPortalAccess={hasAuthorPortalAccess}
                    showGross={false}
                    isPress
                />
            )}
            <div className="article-detail-content-head">
                <div className="article-detail-content-header">
                    <h1 title={article.name}>{article.name}</h1>
                    <div className="article-detail-content-actions">
                        {["audiovisual", "press"].includes(portalType) && (
                            <StyledButton buttonType="secondary" onClick={() => onEditMode()}>
                                <Edit />
                                {t("Articles.Edit")}
                            </StyledButton>
                        )}
                        <StyledButton buttonType="secondary" onClick={() => onClose()}>
                            <ActionCrossSign />
                        </StyledButton>
                    </div>
                </div>
                {["audiovisual", "press"].includes(portalType) && (
                    <>
                        <div className="article-detail-content-infos">
                            <div className="article-detail-content-info">
                                <Body color="grey">
                                    {t("Articles.From")}{" "}
                                    <span onClick={() => navigate(`/beneficiars/${article.beneficiaryId}`)}>
                                        {article.beneficiary}
                                    </span>{" "}
                                    {t("Articles.On")}{" "}
                                    <span onClick={() => navigate(`/publications/${article.titleId}`)}>
                                        {article.titleName}
                                    </span>
                                </Body>
                            </div>
                            <div className="article-detail-content-price">
                                <BodyHighlight color="grey">
                                    {isFolioBased
                                        ? t("Articles.FolioAt", {
                                              nbFolio: article.packageQuantity,
                                              folioPrice: formatNumber((article.price ?? 0) / 100, {
                                                  isCurrency: true,
                                              }),
                                              count: +article.packageQuantity,
                                          })
                                        : t("Articles.AmountAt", {
                                              price: formatNumber((article.packageQuantity ?? 0) / 100, {
                                                  isCurrency: true,
                                              }),
                                          })}
                                </BodyHighlight>
                                <StatusSelect
                                    value={paidStatus ?? 0}
                                    options={dropdownPaymentOptions()}
                                    onChange={(newPaymentValue: 0 | 1 | 2) =>
                                        handlePaymentStatus(newPaymentValue, article.id)
                                    }
                                    colorRules={paymentColorRules}
                                    disabled={disablePaymentSelect(paidStatus ?? 0)}
                                />
                            </div>
                            <SmallBody color="grey">
                                {t("Articles.WorkNature")} {article.workNature || "-"}
                            </SmallBody>
                            {usesIRCEC && (
                                <SmallBody color="grey">
                                    {t("Articles.WorkNatureIRCEC")} {workNatureIRCEC || "-"}
                                </SmallBody>
                            )}
                            {article.completionDate && (
                                <SmallBody color="grey">
                                    {t("Articles.CompletionDate")}:{" "}
                                    {formatLocaleDateString({
                                        date: article.completionDate,
                                        options: { numeric: true, timeZone: "UTC" },
                                    })}
                                </SmallBody>
                            )}
                        </div>

                        {(portalType === "beneficiary" || usesAgreements) && (
                            <div style={{ marginTop: "24px" }}>
                                <LoadingWrapper loading={sendSignatureStatus === "pending"}>
                                    {portalType === "beneficiary" ? (
                                        <BPArticleAgreements articleId={article.id} />
                                    ) : (
                                        <ContractsCounterBlock
                                            agreements={articleAgreements}
                                            resourceType="article"
                                            resourceId={article.id}
                                            parentId={article.titleId ?? undefined}
                                            loading={agreementsStatus !== "succeeded"}
                                            primaryAction={
                                                <ButtonMenu
                                                    mainActions={{
                                                        icon: <Plus />,
                                                        name: t("ContractSettings.NewDocumentForSignature"),
                                                    }}
                                                    actions={
                                                        [
                                                            {
                                                                name: t(
                                                                    "ContractSettings.SendForSignatureViaProvider",
                                                                    {
                                                                        provider: activeSignProvider.label,
                                                                    },
                                                                ),
                                                                action: () => handleShowAgreementModal(),
                                                            },
                                                            {
                                                                name: t("ShowDetails.CreateAvenant"),
                                                                action: () =>
                                                                    navigate("/agreements/new", {
                                                                        state: {
                                                                            relatedResourceData: {
                                                                                relatedResource: "article",
                                                                                relatedResourceId: article.id,
                                                                                beneficiaryId: article.beneficiaryId,
                                                                                titleId: article.titleId,
                                                                                title: article.name ?? "",
                                                                                beneficiary: article.beneficiary ?? "",
                                                                                email: article.email,
                                                                            },
                                                                            documentType: "avenant",
                                                                            signatureProvider: activeSignProvider.key,
                                                                        } as NewAgreementLocationState,
                                                                    }),
                                                            },
                                                            {
                                                                name: t("ContractSettings.AddManuallySignedContract"),
                                                                action: () => setShowUploadContractModal(true),
                                                            },
                                                        ] as Action[]
                                                    }
                                                    showExpandBtn={false}
                                                    fullWidth
                                                />
                                            }
                                        />
                                    )}
                                </LoadingWrapper>
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="article-detail-content-body">
                <div className="show-statement-actions">
                    {!editMode && (
                        <>
                            {["audiovisual", "press"].includes(portalType) && (
                                <StyledButton
                                    buttonType="outlined"
                                    disabled={(hasAuthorPortalAccess && !!article.lastSentAt) || !article.email}
                                    onClick={() => setIsEmailModalOpen(true)}
                                >
                                    <ActionSend className="show-statement-actions-svg" />
                                    {disabledSendText}
                                </StyledButton>
                            )}
                            <StyledButton buttonType="primary" onClick={savePdf} disabled={savingStatus == "pending"}>
                                {savingStatus == "pending" ? (
                                    <CircularProgress size={24} sx={{ color: "#fefefe" }} />
                                ) : (
                                    <>
                                        <ActionDownload className="show-statement-actions-svg" />
                                        {t("ShowStatements.Download")}
                                    </>
                                )}
                            </StyledButton>
                        </>
                    )}
                </div>
                <TabContext value={currentTab.toString()}>
                    <StyledTabs value={+currentTab} onChange={onTabChange}>
                        <Tab label={t("Articles.NetStatement")} />
                        <Tab label={t("Articles.PrecountStatement")} disabled={article.precount == "0"} />
                        {contractTabVisible && <Tab label={t("Articles.Contract")} disabled={contractTabDisabled} />}
                    </StyledTabs>
                    <TabPanel sx={isMobile ? { height: `${muiTabHeight}px`, padding: 0 } : { padding: 0 }} value="0">
                        {statementNotTransmitted ? (
                            <div className="spinner-wrapper" style={{ padding: "48px 24px", textAlign: "center" }}>
                                <Body color="grey">{t("Articles.StatementNotTransmitted")}</Body>
                            </div>
                        ) : (
                            <>
                                {status === "pending" && (
                                    <div className="spinner-wrapper">
                                        <Spinner />
                                    </div>
                                )}
                                {status === "failed" && (
                                    <div className="spinner-wrapper">
                                        <EmptyState type="statementFailed" />
                                    </div>
                                )}
                                {status === "succeeded" && (
                                    <div
                                        style={
                                            isMobile ? { transform: `scale(${scalePDFValue})` } : { maxWidth: "960px" }
                                        }
                                        ref={statementPdfRef}
                                        className="show-pdf-wrapper"
                                        dangerouslySetInnerHTML={{ __html: netStatement }}
                                    ></div>
                                )}
                            </>
                        )}
                    </TabPanel>
                    <TabPanel sx={isMobile ? { height: `${muiTabHeight}px`, padding: 0 } : { padding: 0 }} value="1">
                        {statementNotTransmitted ? (
                            <div className="spinner-wrapper" style={{ padding: "48px 24px", textAlign: "center" }}>
                                <Body color="grey">{t("Articles.StatementNotTransmitted")}</Body>
                            </div>
                        ) : (
                            <>
                                {status === "pending" && (
                                    <div className="spinner-wrapper">
                                        <Spinner />
                                    </div>
                                )}
                                {status === "failed" && (
                                    <div className="spinner-wrapper">
                                        <EmptyState type="statementFailed" />
                                    </div>
                                )}
                                {status === "succeeded" && (
                                    <div
                                        style={
                                            isMobile ? { transform: `scale(${scalePDFValue})` } : { maxWidth: "960px" }
                                        }
                                        ref={statementPdfRef}
                                        className="show-pdf-wrapper"
                                        dangerouslySetInnerHTML={{ __html: precountStatement }}
                                    ></div>
                                )}
                            </>
                        )}
                    </TabPanel>
                    <TabPanel
                        sx={
                            isMobile
                                ? { height: `${muiTabHeight}px`, padding: 0 }
                                : { padding: 0, position: "relative", height: "70%" }
                        }
                        value="2"
                    >
                        {status === "pending" && (
                            <div className="spinner-wrapper">
                                <Spinner />
                                <span className="spinner-text">{t("ShowStatements.Loading")}</span>
                            </div>
                        )}
                        {status === "failed" && (
                            <div className="spinner-wrapper">
                                <EmptyState type="statementFailed" />
                            </div>
                        )}

                        {status === "succeeded" && (
                            <>
                                {hasContractFile(article) ? (
                                    <PDFViewer document={contract} />
                                ) : (
                                    <div
                                        className="article-detail-content-html-contract"
                                        style={isMobile ? { height: `${muiTabHeight}px` } : {}}
                                    >
                                        <div
                                            style={
                                                isMobile
                                                    ? { transform: `scale(${scalePDFValue})` }
                                                    : { maxWidth: "960px" }
                                            }
                                            ref={statementPdfRef}
                                            className="show-pdf-wrapper"
                                            dangerouslySetInnerHTML={{ __html: contract }}
                                        ></div>
                                    </div>
                                )}
                            </>
                        )}
                    </TabPanel>
                </TabContext>
            </div>
            <ModalBox
                open={showUploadContractModal}
                onClose={() => setShowUploadContractModal(false)}
                mainTitle={"Ajouter le PDF d'un contrat signé"}
            >
                <div className="article-detail-content-signature-modal">
                    <div className="main-text">{t("Articles.SendSignatureManualText")}</div>
                    <div style={{ display: "flex", flexFlow: "column", gap: "16px" }}>
                        <DropZoneCSV
                            setFile={setFile}
                            file={file}
                            acceptedFilesTypeList={{ "application/pdf": [".pdf"] }}
                        />
                        <div>
                            <h3>{t("Articles.SignatureDate")}</h3>
                            <StyledDatePicker
                                value={signatureDate}
                                onChange={(value: any) => setSignatureDate(value)}
                                inputFormat="dd/MM/yyyy"
                                renderInput={(props) => <StyledTextField {...props} fullWidth />}
                            />
                        </div>
                    </div>
                    <div className="article-detail-content-signature-modal-actions">
                        <StyledButton
                            buttonType="secondary"
                            fullWidth
                            onClick={() => setShowUploadContractModal(false)}
                        >
                            {t("Articles.Cancel")}
                        </StyledButton>
                        <StyledButton buttonType="primary" fullWidth onClick={handleUploadManualSignedContract}>
                            {t("Articles.Save")}
                        </StyledButton>
                    </div>
                </div>
            </ModalBox>
        </div>
    );
};

export default ArticleDetailContent;
